import { creating, exist, Id, UUID } from '@campaign-portal/namespace/common/id';
import { RoleType } from '@campaign-portal/namespace/common/enums';
import { Permission, User, UserNotifications } from '@campaign-portal/namespace/common/user';

export interface UserEntityConfig<ID = UUID | creating> extends Partial<User<ID>> {
	id: ID;
	partnerId: Id<exist>;
}

export class UserEntity<ID = UUID | creating> implements User<ID> {
	id: ID;
	email: string;
	additionalEmail?: string;
	login: string;
	details: { [key: string]: unknown };

	disabledAt: string | null;
	permissions: Permission[];
	notifications: UserNotifications;

	isActive: boolean;
	partnerId: Id<exist>;
	role: RoleType;

	lastLoginDate?: string;
	isEnable2FA: boolean;
	customFormId?: Id<exist>;
	customFields?: { [fieldVariable: string]: unknown };

	accessiblePartnerIds: Id<exist>[] | null;
	assignedAsManagerForPartnerIds: Id<exist>[];

	readonly creationDate?: string | null;
	readonly createdBy?: string | null;
	readonly lastUpdated?: string | null;
	readonly modifiedBy?: string | null;

	constructor(config: UserEntityConfig<ID>) {
		this.id = config.id;
		this.partnerId = config.partnerId;
		this.email = config.email ?? '';
		this.login = config.login ?? '';
		this.details = config.details ?? { firstName: '', lastName: '' };
		this.disabledAt = config.disabledAt ?? null;
		this.isActive = config.isActive ?? true;
		this.role = config.role ?? RoleType.ADMIN;
		this.permissions = config.permissions ?? [];
		this.notifications = config.notifications ?? { general: [], other: [] };
		this.isEnable2FA = config.isEnable2FA ?? false;
		this.accessiblePartnerIds = config.accessiblePartnerIds ?? null;
		this.assignedAsManagerForPartnerIds = config.assignedAsManagerForPartnerIds ?? [];
		this.lastLoginDate = config.lastLoginDate;
		this.customFormId = config.customFormId;
		this.additionalEmail = config.additionalEmail;
		this.customFields = config.customFields;
		this.creationDate = config.creationDate;
		this.createdBy = config.createdBy;
		this.lastUpdated = config.lastUpdated;
		this.modifiedBy = config.modifiedBy;
	}

	get firstName(): string {
		return (this.details as { firstName?: string }).firstName ?? '';
	}

	get lastName(): string {
		return (this.details as { lastName?: string }).lastName ?? '';
	}

	get name(): string {
		return `${this.firstName} ${this.lastName}`.trim() || this.email;
	}

	isAdmin(): boolean {
		return this.role === RoleType.ADMIN;
	}

	hasUnrestrictedAccess(): boolean {
		return this.accessiblePartnerIds === null;
	}

	canAccessPartner(partnerId: Id<exist>): boolean {
		return this.hasUnrestrictedAccess() || (this.accessiblePartnerIds?.includes(partnerId) ?? false);
	}
}
